import {Input} from '@sproutsocial/racine';
import {type SyntheticEvent, useCallback, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';

const DropdownContainer = styled.div`
	position: absolute;
	top: 100%;
	left: 0;
	right: 0;
	background: white;
	border: 1px solid #d8dce5;
	border-radius: 4px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
	z-index: 1000;
	max-height: 200px;
	overflow-y: auto;
`;

const SuggestionItem = styled.div`
	padding: 8px 12px;
	cursor: pointer;
	font-size: 14px;
	line-height: 1.4;

	&:hover {
		background-color: #f5f7fa;
	}

	&:not(:last-child) {
		border-bottom: 1px solid #f0f0f0;
	}
`;

type TextInputOnChange = (
	event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement> | null,
	newValue?: string,
) => void;

interface AddressAutocompleteProps {
	id: string;
	name: string;
	value: string;
	onChange: TextInputOnChange;
}

const AddressAutocomplete = ({id, name, value, onChange}: AddressAutocompleteProps) => {
	const [suggestions, setSuggestions] = useState<google.maps.places.AutocompletePrediction[]>([]);
	const serviceRef = useRef<any>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		loadGoogleMaps()
			.then(() => {
				serviceRef.current = new google.maps.places.AutocompleteService();
			})
			.catch((error) => {
				console.error(error);
			});
	}, []);

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setSuggestions([]);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const onInputChange: TextInputOnChange = useCallback(
		(event, newValue) => {
			const inputValue = newValue ?? event?.currentTarget.value ?? '';
			onChange(event, newValue);

			if (serviceRef.current && inputValue.length > 2) {
				serviceRef.current.getPlacePredictions(
					{input: inputValue, types: ['address']},
					(predictions: google.maps.places.AutocompletePrediction[] | null) => {
						setSuggestions(predictions ?? []);
					},
				);
			} else {
				setSuggestions([]);
			}
		},
		[onChange],
	);

	const onSelectSuggestion = useCallback(
		(description: string) => {
			onChange(null, description);
			setSuggestions([]);
		},
		[onChange],
	);

	return (
		<div ref={containerRef} style={{position: 'relative'}}>
			<Input id={id} name={name} value={value} onChange={onInputChange} />
			{suggestions.length > 0 && (
				<DropdownContainer>
					{suggestions.map((s) => (
						<SuggestionItem
							key={s.place_id}
							onMouseDown={(e) => {
								e.preventDefault();

								onSelectSuggestion(s.description);
							}}
						>
							{s.description}
						</SuggestionItem>
					))}
				</DropdownContainer>
			)}
		</div>
	);
};

let googleMapsPromise: Promise<void> | null = null;

const loadGoogleMaps = async (): Promise<void> => {
	if (googleMapsPromise) {
		return googleMapsPromise;
	}

	if ((window as any).google?.maps?.places) {
		return;
	}

	const apiKey = 'AIzaSyA-U1eoeX-klStcAymMXNZlPmbGDrPbbh4';

	googleMapsPromise = new Promise((resolve) => {
		(window as any).__googleMapsCallback = resolve;
		const script = document.createElement('script');
		script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=__googleMapsCallback`;
		script.async = true;
		document.head.appendChild(script);
	});

	return googleMapsPromise;
};

export default AddressAutocomplete;
